import type { CapacityCategory } from '@/types/capacityType';

export type PoStatus = 'planned' | 'confirmed' | 'in-production' | 'completed';

export const ASSIGNMENT_TYPE = {
  po: 'po',
  manual: 'manual',
} as const;

export type AssignmentType = (typeof ASSIGNMENT_TYPE)[keyof typeof ASSIGNMENT_TYPE];

// What ASSIGNMENT_TYPE.manual is called in the interface: capacity reserved for something
// other than a purchase order — maintenance, samples, a job whose PO has not landed yet.
// "Manual" is how the API classifies the row, not a word to put in front of a planner, and
// this name has to fit the slot a PO number occupies beside it.
export const RESERVATION_LABEL = 'Reservation';

// An allocation bar also renders the unallocated leftover, which is not an assignment.
export const SEGMENT_TYPE = {
  ...ASSIGNMENT_TYPE,
  remains: 'remains',
} as const;

export type SegmentType = (typeof SEGMENT_TYPE)[keyof typeof SEGMENT_TYPE];

export interface PoAllocation {
  id?: string;
  // undefined is treated as ASSIGNMENT_TYPE.po everywhere this field is read — every
  // pre-existing fixture/call site across the app was written before this field existed.
  type?: AssignmentType;
  // null for a manual block that references no PO; a manual block may carry one as a
  // tracking reference, which is never a PO commitment.
  poNumber: string | null;
  quantity: number;
  status?: PoStatus;
  notes?: string;
  // Shared by every row written in one multi-type distribution; null for manual single assignments.
  distributionGroupId?: string | null;
  productCategory?: string | null;
}

// remainedCapacity is derived (planningCapacity − Σ PO quantities) and cannot be set.
export interface AllocationParametersUpdate {
  planningCapacity: number;
  capacityFactor: number;
}

export interface WeekCapacityAllocation {
  id: string;
  weekKey: string;
  purchaseOrders: PoAllocation[];
  remainedCapacity: number;
  // Vendor-reported raw capacity for the week — what capacityFactor scales into planningCapacity.
  capacity?: number | null;
  lineQty?: number | null;
  vendorInput?: number | null;
  planningCapacity?: number;
  capacityFactor?: number;
  // Vendor-reported remaining from the imported sheet — reference only.
  vendorRemained?: number | null;
  // How the bucket was last written: from an imported plan or entered by hand.
  source?: 'import' | 'manual';
}

export interface CalendarEntry {
  id: string;
  vendorId?: string;
  capacityTypeId?: string;
  vendorCode: string;
  vendorName: string;
  typeCode: string;
  typeLabel: string;
  category: CapacityCategory;
  unit: string | null;
  // Synthetic row for an import-preview line whose vendor+type has no calendar row yet
  // (see buildImportDiff's ghostRows) — never present on real, persisted entries.
  isGhost?: boolean;
}

export interface CalendarRow extends CalendarEntry {
  isFirstInVendorGroup: boolean;
  vendorRowSpan: number;
}

export type AllocationStatus = 'available' | 'full' | 'overbooked';

export interface AllocationSegment {
  key: string;
  type: SegmentType;
  label: string;
  quantity: number;
  percent: number;
  // Only on 'po' segments: the PO's distribution group, null for manual single assignments.
  distributionGroupId?: string | null;
}

export interface AllocationSummary {
  status: AllocationStatus;
  utilizationPercent: number;
  overbookedBy: number;
  segments: AllocationSegment[];
  // Zero-quantity assignments (non-capacity POs): rendered as a fixed hatched slice,
  // never part of segments/percent math.
  nonCapacityPos: PoAllocation[];
}

export interface DayAllocationPlacement {
  weekKey: string;
  visibleDaySpan: number;
}

export interface CalendarCellDisplay {
  allocation: WeekCapacityAllocation | null;
  columnSpan: number;
  hidden: boolean;
}

export type CapacityAllocationMap = Record<string, Record<string, WeekCapacityAllocation>>;

export interface CapacityCalendarData {
  entries: CalendarEntry[];
  allocations: CapacityAllocationMap;
  lockedVendorIds: string[];
}

export interface SelectedPoAllocation {
  po: PoAllocation;
  rowId: string;
  vendorName: string;
  typeLabel: string;
  weekKey: string;
}

export interface SelectedAllocation {
  allocation: WeekCapacityAllocation;
  rowId: string;
  vendorName: string;
  typeLabel: string;
  // Present when the row is linked to portal entities; the PO picker needs them.
  vendorId?: string;
  capacityTypeId?: string;
  typeCode?: string;
  category?: CapacityCategory;
}
